<div class="top-section">
    <h6 class="heading mb-0">{{ ss.t('debug_screenTitle') }}</h6>
    <app-svg name="close" role="button" (click)="closeDialog()"></app-svg>
</div>

<!-- Tab Navigation -->
<div class="tab-nav ri-px-4 ri-pt-3">
    <button class="tab-btn" [class.active]="activeTab() === 'costs'" (click)="setActiveTab('costs')">
        {{ ss.t('debug_cloudCosts') }}
    </button>
</div>

<div class="body-section position-relative ri-py-4">
    <!-- Cloud Costs Tab -->
    @if (activeTab() === 'costs') {
        <!-- Summary View -->
        @if (costView() === 'summary') {
            <div class="cost-summary ri-px-4">
                <!-- Session Info -->
                <div class="session-info flex items-center justify-between ri-mb-4">
                    <span class="text-14-400 color-7">SESSION: {{ formatSessionTime(summary()?.sessionMs || 0) }}</span>
                    <button class="reset-btn text-14-400 color-35" (click)="resetCostSession()">{{ ss.t('common_reset') }}</button>
                </div>

                <!-- All Infrastructure Summary -->
                <div class="infrastructure-card ri-p-3 ri-mb-4">
                    <h6 class="text-14-600 color-8 ri-mb-3">{{ ss.t('debug_allInfrastructure') }}</h6>

                    @if (summary()) {
                        <div class="infra-row flex items-center justify-between ri-py-2" (click)="setCostView('firestore')" role="button">
                            <div class="flex items-center">
                                <span class="infra-icon">FS</span>
                                <span class="text-14-400 color-8 ri-ms-2">{{ ss.t('debug_firestore') }}</span>
                            </div>
                            <div class="flex items-center gap-3">
                                <span class="text-12-400 color-7">{{ summary()!.firestore.reads }} reads | {{ summary()!.firestore.writes }} writes</span>
                                <span class="text-12-400 color-35">{{ formatCost(summary()!.firestore.cost) }}</span>
                            </div>
                        </div>

                        <div class="infra-row flex items-center justify-between ri-py-2" (click)="setCostView('storage')" role="button">
                            <div class="flex items-center">
                                <span class="infra-icon">ST</span>
                                <span class="text-14-400 color-8 ri-ms-2">{{ ss.t('debug_storage') }}</span>
                            </div>
                            <div class="flex items-center gap-3">
                                <span class="text-12-400 color-7">{{ summary()!.storage.uploads + summary()!.storage.downloads }} files | {{ formatBytes(summary()!.storage.uploadBytes + summary()!.storage.downloadBytes) }}</span>
                                <span class="text-12-400 color-35">{{ formatCost(summary()!.storage.cost) }}</span>
                            </div>
                        </div>

                        <div class="infra-row flex items-center justify-between ri-py-2" (click)="setCostView('rtdb')" role="button">
                            <div class="flex items-center">
                                <span class="infra-icon">RT</span>
                                <span class="text-14-400 color-8 ri-ms-2">{{ ss.t('debug_rtdb') }}</span>
                            </div>
                            <div class="flex items-center gap-3">
                                <span class="text-12-400 color-7">{{ summary()!.rtdb.attachments }} listeners | {{ summary()!.rtdb.dataEvents }} events | {{ formatBytes(summary()!.rtdb.bytes) }}</span>
                                <span class="text-12-400 color-35">{{ formatCost(summary()!.rtdb.cost) }}</span>
                            </div>
                        </div>

                        <div class="infra-row flex items-center justify-between ri-py-2" (click)="setCostView('functions')" role="button">
                            <div class="flex items-center">
                                <span class="infra-icon">FN</span>
                                <span class="text-14-400 color-8 ri-ms-2">{{ ss.t('debug_functions') }}</span>
                            </div>
                            <div class="flex items-center gap-3">
                                <span class="text-12-400 color-7">{{ summary()!.functions.calls }} calls | {{ formatDuration(summary()!.functions.totalMs) }}</span>
                                <span class="text-12-400 color-35">{{ formatCost(summary()!.functions.cost) }}</span>
                            </div>
                        </div>

                        <div class="total-row flex items-center justify-end ri-pt-3 ri-mt-2">
                            <span class="text-14-600 color-8">Total: {{ formatCost(summary()!.totalCost) }}</span>
                        </div>
                    }
                </div>

                <!-- Top Operations -->
                @if (topOperations().length > 0) {
                    <div class="top-ops-card ri-p-3">
                        <h6 class="text-14-600 color-8 ri-mb-3">{{ ss.t('debug_topResourceConsumers') }}</h6>
                        @for (op of topOperations(); track op.key; let i = $index) {
                            <div class="top-op-row flex items-center justify-between ri-py-2">
                                <div class="flex items-center">
                                    <span class="text-12-400 color-7 ri-me-2">{{ i + 1 }}.</span>
                                    <span class="text-12-400 color-8">{{ op.key }}</span>
                                </div>
                                <div class="flex items-center gap-2">
                                    <span class="text-12-400 color-7">{{ op.metric }}</span>
                                    <span class="text-12-400 color-35">{{ op.percentage?.toFixed(1) }}%</span>
                                </div>
                            </div>
                        }
                    </div>
                }
            </div>
        }

        <!-- Firestore View -->
        @if (costView() === 'firestore') {
            <div class="detail-view ri-px-4">
                <div class="detail-header flex items-center ri-mb-4">
                    <button class="back-btn text-14-400 color-35" (click)="goBack()">{{ ss.t('common_back') }}</button>
                    <h6 class="text-16-600 color-8 ri-ms-3">{{ ss.t('debug_firestore') }}</h6>
                </div>

                @if (summary()) {
                    <div class="detail-summary text-14-400 color-7 ri-mb-4">
                        Total: {{ summary()!.firestore.reads }} reads | {{ summary()!.firestore.writes }} writes | {{ summary()!.firestore.deletes }} deletes
                        <span class="color-35 ri-ms-2">{{ formatCost(summary()!.firestore.cost) }}</span>
                    </div>
                }

                <h6 class="text-12-600 color-7 ri-mb-2">{{ ss.t('debug_byCollection') }}</h6>
                @for (col of firestoreBreakdown(); track col.collection) {
                    <div class="collection-card ri-mb-2">
                        <div class="collection-header flex items-center justify-between ri-p-3" (click)="toggleCollection(col.collection)" role="button">
                            <div class="flex items-center">
                                <span class="expand-icon">{{ isCollectionExpanded(col.collection) ? '-' : '+' }}</span>
                                <span class="text-14-400 color-8 ri-ms-2">{{ col.collection }}</span>
                            </div>
                            <span class="text-12-400 color-7">({{ col.totalReads }} reads, {{ col.totalWrites }} writes)</span>
                        </div>
                        @if (isCollectionExpanded(col.collection)) {
                            <div class="collection-features ri-px-4 ri-pb-3">
                                @for (f of col.features; track f.feature) {
                                    <div class="feature-row flex items-center justify-between ri-py-1">
                                        <span class="text-12-400 color-7">{{ f.feature }}</span>
                                        <span class="text-12-400 color-7">{{ f.reads }} reads, {{ f.writes }} writes ({{ f.percentage.toFixed(1) }}%)</span>
                                    </div>
                                }
                            </div>
                        }
                    </div>
                }
            </div>
        }

        <!-- Storage View -->
        @if (costView() === 'storage') {
            <div class="detail-view ri-px-4">
                <div class="detail-header flex items-center ri-mb-4">
                    <button class="back-btn text-14-400 color-35" (click)="goBack()">{{ ss.t('common_back') }}</button>
                    <h6 class="text-16-600 color-8 ri-ms-3">{{ ss.t('debug_cloudStorage') }}</h6>
                </div>

                @if (summary()) {
                    <div class="detail-summary text-14-400 color-7 ri-mb-4">
                        Total: {{ summary()!.storage.uploads }} uploads ({{ formatBytes(summary()!.storage.uploadBytes) }}) | {{ summary()!.storage.downloads }} downloads ({{ formatBytes(summary()!.storage.downloadBytes) }})
                        <span class="color-35 ri-ms-2">{{ formatCost(summary()!.storage.cost) }}</span>
                    </div>
                }

                <h6 class="text-12-600 color-7 ri-mb-2">{{ ss.t('debug_uploads') }}</h6>
                @for (item of getStorageUploads(); track item.feature) {
                    <div class="storage-row flex items-center justify-between ri-py-2 ri-px-3">
                        <span class="text-14-400 color-8">{{ item.feature }}</span>
                        <span class="text-12-400 color-7">{{ item.count }} files ({{ formatBytes(item.bytes) }})</span>
                    </div>
                }
                @if (getStorageUploads().length === 0) {
                    <div class="text-12-400 color-7 ri-py-2">{{ ss.t('debug_noUploadsThisSession') }}</div>
                }

                <h6 class="text-12-600 color-7 ri-mb-2 ri-mt-4">{{ ss.t('debug_downloads') }}</h6>
                @for (item of getStorageDownloads(); track item.feature) {
                    <div class="storage-row flex items-center justify-between ri-py-2 ri-px-3">
                        <span class="text-14-400 color-8">{{ item.feature }}</span>
                        <span class="text-12-400 color-7">{{ item.count }} files ({{ formatBytes(item.bytes) }})</span>
                    </div>
                }
                @if (getStorageDownloads().length === 0) {
                    <div class="text-12-400 color-7 ri-py-2">{{ ss.t('debug_noDownloadsThisSession') }}</div>
                }
            </div>
        }

        <!-- RTDB View -->
        @if (costView() === 'rtdb') {
            <div class="detail-view ri-px-4">
                <div class="detail-header flex items-center ri-mb-4">
                    <button class="back-btn text-14-400 color-35" (click)="goBack()">{{ ss.t('common_back') }}</button>
                    <h6 class="text-16-600 color-8 ri-ms-3">{{ ss.t('debug_realtimeDatabase') }}</h6>
                </div>

                @if (summary()) {
                    <div class="detail-summary text-14-400 color-7 ri-mb-4">
                        Total: {{ summary()!.rtdb.attachments }} listeners | {{ summary()!.rtdb.dataEvents }} data events | {{ formatBytes(summary()!.rtdb.bytes) }}
                        <span class="color-35 ri-ms-2">{{ formatCost(summary()!.rtdb.cost) }}</span>
                    </div>
                }

                <!-- Changes by Collection -->
                <h6 class="text-12-600 color-7 ri-mb-2">{{ ss.t('debug_changesByCollection') }}</h6>
                @for (change of rtdbChanges(); track change.collection) {
                    <div class="rtdb-card ri-p-3 ri-mb-2">
                        <div class="flex items-center justify-between ri-mb-1">
                            <span class="text-14-400 color-8">{{ change.collection }}</span>
                            <span class="text-14-600 color-1">{{ change.totalChanges }} changes</span>
                        </div>
                        <div class="text-12-400 color-7">
                            Owned: {{ change.ownedChanges }} | Shared: {{ change.sharedChanges }}
                        </div>
                    </div>
                }
                @if (rtdbChanges().length === 0) {
                    <div class="text-12-400 color-7 ri-py-2 ri-mb-4">{{ ss.t('debug_noCollectionChanges') }}</div>
                }

                <h6 class="text-12-600 color-7 ri-mb-2 ri-mt-4">{{ ss.t('debug_byFeature') }}</h6>
                @for (item of getRtdbFeatures(); track item.feature) {
                    <div class="rtdb-card ri-p-3 ri-mb-2">
                        <div class="text-14-400 color-8 ri-mb-2">{{ item.feature }}</div>
                        <div class="text-12-400 color-7">
                            Listeners: {{ item.data.attachments }} | Events: {{ item.data.dataEvents }} | Data: {{ formatBytes(item.data.bytes) }}
                        </div>
                    </div>
                }
                @if (getRtdbFeatures().length === 0) {
                    <div class="text-12-400 color-7 ri-py-2">{{ ss.t('debug_noRtdbActivity') }}</div>
                }
            </div>
        }

        <!-- Functions View -->
        @if (costView() === 'functions') {
            <div class="detail-view ri-px-4">
                <div class="detail-header flex items-center ri-mb-4">
                    <button class="back-btn text-14-400 color-35" (click)="goBack()">{{ ss.t('common_back') }}</button>
                    <h6 class="text-16-600 color-8 ri-ms-3">{{ ss.t('debug_cloudFunctions') }}</h6>
                </div>

                @if (summary()) {
                    <div class="detail-summary text-14-400 color-7 ri-mb-4">
                        Total: {{ summary()!.functions.calls }} calls | {{ formatDuration(summary()!.functions.totalMs) }} total | {{ summary()!.functions.errors }} errors
                        <span class="color-35 ri-ms-2">{{ formatCost(summary()!.functions.cost) }}</span>
                    </div>
                }

                <h6 class="text-12-600 color-7 ri-mb-2">{{ ss.t('debug_byFunction') }}</h6>
                @for (fn of functionBreakdown(); track fn.fnName) {
                    <div class="function-card ri-mb-2">
                        <div class="function-header flex items-center justify-between ri-p-3" (click)="toggleFunction(fn.fnName)" role="button">
                            <div class="flex items-center">
                                <span class="expand-icon">{{ isFunctionExpanded(fn.fnName) ? '-' : '+' }}</span>
                                <span class="text-14-400 color-8 ri-ms-2">{{ fn.fnName }}</span>
                            </div>
                            <span class="text-12-400 color-7">({{ fn.totalCalls }} calls, avg {{ formatDuration(fn.avgMs) }})</span>
                        </div>
                        @if (isFunctionExpanded(fn.fnName)) {
                            <div class="function-features ri-px-4 ri-pb-3">
                                @for (f of fn.features; track f.feature) {
                                    <div class="feature-row flex items-center justify-between ri-py-1">
                                        <span class="text-12-400 color-7">{{ f.feature }}</span>
                                        <span class="text-12-400 color-7">{{ f.calls }} calls, avg {{ formatDuration(f.avgMs) }}{{ f.errors > 0 ? ' (' + f.errors + ' errors)' : '' }}</span>
                                    </div>
                                }
                            </div>
                        }
                    </div>
                }
                @if (functionBreakdown().length === 0) {
                    <div class="text-12-400 color-7 ri-py-2">No function calls this session</div>
                }
            </div>
        }
    }
</div>
